import { ApiCacheTags, baseApiService } from '@qapline/api';
import { DEFAULT_CONFIG } from '@qapline/config';
import { z } from 'zod';

import { extendedInvoiceSchema, TExtendedInvoice } from '../schemas';
import {
    TCreateInvoiceRequestBody,
    TListInvoicesRequestQuery,
    TPreviewInvoicePdfRequestBody,
    TUpdateInvoiceRequestBody,
} from '../types';

const invoiceEndpoints = DEFAULT_CONFIG.api.endpoints.invoice;
const invoiceCacheTags = ApiCacheTags.invoice;

export const invoiceApi = baseApiService.injectEndpoints({
    endpoints: (builder) => ({
        listInvoices: builder.query<{ data: TExtendedInvoice[]; count: number }, TListInvoicesRequestQuery>({
            query: ({ filters, order, search, skip, take }) => ({
                url: invoiceEndpoints.list,
                method: 'GET',
                params: {
                    filters: filters ? JSON.stringify(filters) : undefined,
                    order: order ? JSON.stringify(order) : undefined,
                    search,
                    skip,
                    take,
                },
            }),
            providesTags: (_res, error, req) =>
                error ? [] : [{ type: invoiceCacheTags.list, id: JSON.stringify(req) }],
            transformResponse: (response) =>
                z.object({ data: z.array(extendedInvoiceSchema), count: z.number() }).parse(response),
        }),

        getInvoice: builder.query<TExtendedInvoice, { invoiceId: number }>({
            query: ({ invoiceId }) => ({
                url: invoiceEndpoints.get(invoiceId),
                method: 'GET',
            }),
            providesTags: (_res, error, arg) => (error ? [] : [{ type: invoiceCacheTags.item, id: arg.invoiceId }]),
            transformResponse: (response) => extendedInvoiceSchema.parse(response),
        }),

        createInvoice: builder.mutation<TExtendedInvoice, TCreateInvoiceRequestBody>({
            query: (body) => ({
                url: invoiceEndpoints.create,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_res, error) =>
                error
                    ? []
                    : [
                          invoiceCacheTags.list,
                          invoiceCacheTags.commissionsForInvoicing,
                          invoiceCacheTags.invoicingStatuses,
                          invoiceCacheTags.invoicingStatusCounts,
                      ],
            transformResponse: (response) => extendedInvoiceSchema.parse(response),
        }),

        updateInvoice: builder.mutation<TExtendedInvoice, { invoiceId: number; body: TUpdateInvoiceRequestBody }>({
            query: ({ invoiceId, body }) => ({
                url: invoiceEndpoints.update(invoiceId),
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_res, error, { invoiceId }) =>
                error
                    ? []
                    : [
                          invoiceCacheTags.list,
                          { type: invoiceCacheTags.item, id: invoiceId },
                          { type: ApiCacheTags.attachment.list, id: invoiceId },
                      ],
            transformResponse: (response) => extendedInvoiceSchema.parse(response),
        }),

        cancelInvoice: builder.mutation<TExtendedInvoice, { invoiceId: number }>({
            query: ({ invoiceId }) => ({
                url: invoiceEndpoints.cancel(invoiceId),
                method: 'POST',
            }),
            invalidatesTags: (_res, error, { invoiceId }) =>
                error
                    ? []
                    : [
                          invoiceCacheTags.list,
                          { type: invoiceCacheTags.item, id: invoiceId },
                          invoiceCacheTags.invoicingStatuses,
                          invoiceCacheTags.invoicingStatusCounts,
                      ],
            transformResponse: (response) => extendedInvoiceSchema.parse(response),
        }),

        exportInvoicesToXml: builder.mutation<Blob, { invoiceIds: number[] }>({
            query: ({ invoiceIds }) => ({
                url: invoiceEndpoints.exportXml,
                method: 'POST',
                body: { invoiceIds },
                responseHandler: async (response) => response,
            }),
            // The export stamps the invoices as exported, so the list has to be re-read.
            invalidatesTags: (_res, error) => (error ? [] : [invoiceCacheTags.list]),
            transformResponse: async (response: Response) => response.blob(),
        }),

        /**
         * Renders the PDF for the current, unsaved state of the invoice form. Writes nothing, so it
         * carries no cache tags and can be called on every change to drive a live preview.
         */
        previewInvoicePdf: builder.mutation<Blob, TPreviewInvoicePdfRequestBody>({
            query: (body) => ({
                url: invoiceEndpoints.preview,
                method: 'POST',
                body,
                responseHandler: async (response) => response,
            }),
            transformResponse: async (response: Response) => response.blob(),
        }),

        deleteInvoice: builder.mutation<void, { invoiceId: number }>({
            query: ({ invoiceId }) => ({
                url: invoiceEndpoints.delete(invoiceId),
                method: 'DELETE',
            }),
            invalidatesTags: (_res, error) =>
                error
                    ? []
                    : [
                          invoiceCacheTags.list,
                          invoiceCacheTags.commissionsForInvoicing,
                          invoiceCacheTags.invoicingStatuses,
                          invoiceCacheTags.invoicingStatusCounts,
                      ],
        }),
    }),
});

export const {
    useListInvoicesQuery,
    useLazyListInvoicesQuery,
    useGetInvoiceQuery,
    useLazyGetInvoiceQuery,
    useCreateInvoiceMutation,
    useUpdateInvoiceMutation,
    useCancelInvoiceMutation,
    useDeleteInvoiceMutation,
    useExportInvoicesToXmlMutation,
    usePreviewInvoicePdfMutation,
} = invoiceApi;
